class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
    this.setDesktopCartAccessibility();
    this.updateDesktopCartCount();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setDesktopCartAccessibility() {
    const desktopCartLink = document.querySelector('.bbc-desktop-cart-text');
    if (!desktopCartLink) return;

    desktopCartLink.setAttribute('role', 'button');
    desktopCartLink.setAttribute('aria-haspopup', 'dialog');

    desktopCartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(desktopCartLink);
    });

    desktopCartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(desktopCartLink);
      }
    });
  }

  async updateDesktopCartCount() {
    const desktopCartLink = document.querySelector('.bbc-desktop-cart-text');
    if (!desktopCartLink) return;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) return;

      const cart = await response.json();

      desktopCartLink.textContent = `Cart (${cart.item_count})`;
    } catch (error) {
      console.error('Unable to update cart count:', error);
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    /*
      Synchronise the desktop Cart (x) text whenever
      the drawer is opened.
    */
    this.updateDesktopCartCount();

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    removeTrapFocus(this.activeElement);

    document.body.classList.remove('overflow-hidden');

    /*
      Important:
      Update the visible desktop count immediately
      when the drawer is closed.
    */
    this.updateDesktopCartCount();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (
      drawerInner &&
      drawerInner.classList.contains('is-empty')
    ) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHTML =
        parsedState.sections &&
        parsedState.sections[section.id];

      if (!sectionHTML) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    });

    /*
      The normal Dawn section refresh updates the
      mobile cart icon. Our desktop text is custom,
      so update it separately from Shopify's cart JSON.
    */
    this.updateDesktopCartCount();

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }

      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsedHTML.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define(
  'cart-drawer',
  CartDrawer
);


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateLiveRegions(line, message) {
    super.updateLiveRegions(line, message);

    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      cartDrawer.updateDesktopCartCount();
    }
  }
}

customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);


/*
  Extra synchronisation for quantity changes and removals.

  Dawn replaces parts of the drawer asynchronously.
  After those cart actions finish, synchronise the custom
  desktop Cart (x) label with the real Shopify cart.
*/
document.addEventListener('change', (event) => {
  if (!event.target.closest('cart-drawer')) return;

  window.setTimeout(() => {
    const cartDrawer =
      document.querySelector('cart-drawer');

    if (cartDrawer) {
      cartDrawer.updateDesktopCartCount();
    }
  }, 500);
});


document.addEventListener('click', (event) => {
  if (
    !event.target.closest(
      'cart-drawer cart-remove-button'
    )
  ) {
    return;
  }

  window.setTimeout(() => {
    const cartDrawer =
      document.querySelector('cart-drawer');

    if (cartDrawer) {
      cartDrawer.updateDesktopCartCount();
    }
  }, 500);
});